import React, { useContext, useEffect, useState } from 'react';
import { vfsHomeUrl } from 'src/common';
import { ErrorModal } from 'src/components';
import MiniAppEvents from 'src/helpers/miniapp-events';
import TealiumEvents from 'src/helpers/tealium-events';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { SalesforceContext } from 'src/context/salesforce-context';
import ButtonGroup, { ButtonConfig, LayoutType } from 'src/components/button-group/button-group';
import { ButtonParamType, DefaultButtonStyle } from 'src/components/button/button';
import useStateLocalPersist from 'src/hooks/use-state-local-persist';
import { ReducerContext } from 'src/context/reducer-context';
import ImageCapture from 'src/components/image-capture/image-capture';
import { WizardMessageNames } from '../../../state/wizard/wizard-message-names';
import './webcam-checker.scss';

export default function WebcamChecker() {
  const { dispatch } = useContext(ReducerContext);

  enum WebcamCheckerStatus {
    NOT_STARTED_YET,
    IN_PROGRESS,
    WEBCAM_FOUND,
    WEBCAM_ERROR,
  }

  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const [showErrorModalMiniApp, setShowErrorModalMiniApp] = useStateLocalPersist(
    'show-error-modal-mini-app',
    true,
  );
  const { merchant, customerType } = useContext(SalesforceContext);
  const [webcamCheckerStatus, setWebcamCheckerStatus] = useState<WebcamCheckerStatus>(
    WebcamCheckerStatus.NOT_STARTED_YET,
  );

  useEffect(() => {
    switch (webcamCheckerStatus) {
      case WebcamCheckerStatus.NOT_STARTED_YET:
      case WebcamCheckerStatus.IN_PROGRESS:
        dispatch(WizardMessageNames.SHOW_PAGE_LOADER);
        break;
      case WebcamCheckerStatus.WEBCAM_ERROR:
        TealiumEvents.missingWebcamModalView();
        dispatch(WizardMessageNames.HIDE_PAGE_LOADER);
        break;
      case WebcamCheckerStatus.WEBCAM_FOUND:
        dispatch(WizardMessageNames.HIDE_PAGE_LOADER);
        break;
    }
  }, [webcamCheckerStatus]);

  useEffect(() => {
    if (merchant?.account?.business_type) {
      if (showErrorModalMiniApp) {
        TealiumEvents.chooseYourSolutionMiniAppCameraModal(
          merchant.account.business_type,
          customerType,
        );
      }
    }
  }, [merchant?.account?.business_type]);

  const buildModalButtonsMiniApp = () => {
    const myCameraIsEnabled: ButtonConfig = getButtonConfig(
      'Camera already enabled',
      () => {
        TealiumEvents.cameraEnabledButtonClickMiniApp(customerType);
        setShowErrorModalMiniApp(false);
      },
      true,
      'Onboarding_Business_Type_Button_Modal_My_Camera_Is_Enabled',
    );

    const enableNow: ButtonConfig = getButtonConfig(
      'Enable camera now',
      () => {
        if (inMiniProgram) {
          TealiumEvents.cameraEnableNowButtonClickMiniApp(customerType);
          MiniAppEvents.sendMessage({
            messageType: 'camera_permission',
          });
          setShowErrorModalMiniApp(false);

          MiniAppEvents.listenForMessage((observer) => {
            const message = MiniAppEvents.readMessage();
            if (message.type === 'camera_permission') {
              observer.disconnect();
              if (message.data) {
                toastMessageServiceSingleton.addToast({
                  message: `Your camera has been enabled.`,
                  type: 'info',
                });
              }
            }
          });
        }
      },
      false,
      'Onboarding_Business_Type_Button_Modal_Enable_My_Webcam',
    );

    return (
      <ButtonGroup buttons={[myCameraIsEnabled, enableNow]} layoutType={LayoutType.CENTERED} />
    );
  };

  const buildModalButtons = () => {
    const buttonEnable: ButtonConfig = {
      automationId: 'Onboarding_Button_Choose_Solution_Modal_Enable_My_Webcam',

      label: 'Yes, webcam enabled',
      buttonStyle: DefaultButtonStyle().hasSecondaryAction().isMediumSize().hasLightMode().build(),
      className: 'modal-button',
      buttonParams: {
        type: ButtonParamType.NormalButton,
        onClick: () => {
          TealiumEvents.cameraEnabledButton();
          window.location.reload();
        },
      },
    };

    const buttonBeBackLater: ButtonConfig = {
      automationId: 'Onboarding_Button_Choose_Solution_Modal_Be_Back_Later',
      label: 'I’ll be back later',
      buttonStyle: DefaultButtonStyle().hasPrimaryAction().isMediumSize().hasLightMode().build(),
      className: 'modal-button',
      buttonParams: {
        type: ButtonParamType.NormalButton,
        onClick: () => {
          TealiumEvents.beBackLater();
          window.location.href = vfsHomeUrl;
        },
      },
    };

    return (
      <ButtonGroup buttons={[buttonEnable, buttonBeBackLater]} layoutType={LayoutType.CENTERED} />
    );
  };

  function getButtonConfig(
    label: string,
    onClick: () => void,
    isPrimary: boolean,
    automationId: string,
  ): ButtonConfig {
    const buttonStyle = DefaultButtonStyle().isMediumSize().hasLightMode();

    return {
      automationId: automationId,
      label: label,
      buttonStyle: isPrimary
        ? buttonStyle.hasPrimaryAction().build()
        : buttonStyle.hasSecondaryAction().build(),
      className: 'modal-button',
      buttonParams: {
        type: ButtonParamType.NormalButton,
        onClick: onClick,
      },
    };
  }

  return inMiniProgram ? (
    <div className="WebcamChecker">
      <ErrorModal
        show={showErrorModalMiniApp}
        title="Camera enabled?"
        body={() => (
          <p>
            Your selfie helps us to protect you from identity fraud and verify your identity with
            the Department of Home Affairs. Please enable your camera to continue.
          </p>
        )}
        footer={() => buildModalButtonsMiniApp()}
      />
    </div>
  ) : (
    <div className="WebcamChecker">
      <ImageCapture
        onDeviceAccessed={() => setWebcamCheckerStatus(WebcamCheckerStatus.WEBCAM_FOUND)}
        onDeviceAccessDenied={() => setWebcamCheckerStatus(WebcamCheckerStatus.WEBCAM_ERROR)}
        onDeviceNotFound={() => setWebcamCheckerStatus(WebcamCheckerStatus.WEBCAM_ERROR)}
      />

      <ErrorModal
        show={webcamCheckerStatus === WebcamCheckerStatus.WEBCAM_ERROR}
        title="Enable webcam to continue"
        body={() => (
          <div className="body-style content-large-regular">
            <p>
              We need your photo to protect you from identity fraud and verify your identity with
              the Department of Home Affairs.
            </p>
            <div className="bar" />
          </div>
        )}
        footer={() => buildModalButtons()}
      />
    </div>
  );
}
